<!-- v8 ignore file -->
<script setup lang="ts">
/* v8 ignore file */
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { consola } from 'consola';
import { useProductModalAnalytics } from '../model/useProductModalAnalytics';
import { useCartStore } from '@entities/order';
import { useInstitutionStore } from '@entities/institution';
import { productVariantLabel } from '@entities/product';
import { useCartProduct } from '@features/basket';
import { useModalStore } from '@shared/model';

const emit = defineEmits(['update:size']);

const SHOW_LIMIT = 8;

const storeModal = useModalStore();
const cartStore = useCartStore();
const institutionStore = useInstitutionStore();

const placeholderImg = computed(() => (institutionStore.institution as any)?.visual?.placeholder_file);

const selectedVariantIndex = ref(0);

// Вычисляем индекс варианта на основе ID из store
const computedVariantIndex = computed(() => {
  const dish = cartStore.selectedDish as any;
  const preferredId = cartStore.preferredVariantId;
  
  consola.log('[MoreAboutDish] computedVariantIndex - dish type:', dish?.type);
  consola.log('[MoreAboutDish] computedVariantIndex - preferredId:', preferredId);
  
  if (!dish || dish.type !== 'group' || !Array.isArray(dish.variants)) {
    consola.log('[MoreAboutDish] computedVariantIndex - not a group or no variants, returning 0');
    return 0;
  }
  
  consola.log('[MoreAboutDish] computedVariantIndex - variants:', dish.variants.map((v: any) => ({ id: v.id, name: v.name })));
  
  // Если есть предпочитаемый ID, находим его индекс
  if (preferredId !== null && preferredId !== undefined) {
    const index = dish.variants.findIndex((v: any) => String(v.id) === String(preferredId));
    consola.log('[MoreAboutDish] computedVariantIndex - found index:', index, 'for preferredId:', preferredId);
    return index === -1 ? 0 : index;
  }
  
  consola.log('[MoreAboutDish] computedVariantIndex - no preferredId, returning 0');
  return 0;
});

const currentVariant = computed(() => {
  const dish = cartStore.selectedDish as any;
  if (dish?.type === 'group' && Array.isArray(dish.variants)) {
    return dish.variants[selectedVariantIndex.value];
  }
  return cartStore.selectedDish;
});

const variantTabs = computed(
  () =>
    (cartStore.selectedDish as any)?.variants?.map((variant: any, idx: number) => ({
      key: idx,
      label: productVariantLabel(variant),
    })) || [],
);

const selectedAddons = ref<number[]>([]);
const selectedExcludes = ref<number[]>([]);

const showAllAddons = ref(false);
const showAllExcludes = ref(false);

// Переменная для управления показом блока настроек на мобильных
const showSettings = ref(false);

// Переменная для управления показом блока информации о продукте на мобильных
const showProductInfo = ref(false);

// Переменные для свайпа блока настроек
const touchStartY = ref(0);
const touchEndY = ref(0);
const settingsBlockRef = ref<HTMLElement | null>(null);
const scrollInnerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const dragOffset = ref(0);
const isScrollingInner = ref(false); // Флаг: идет ли скролл внутреннего блока

// Переменные для слайдера медиа (изображения и видео)
const swiperContainerRef = ref<HTMLElement | null>(null);
const videoRefs = ref<{ [key: number]: HTMLVideoElement | null }>({});
const currentSlideIndex = ref(0);

const visibleAddons = computed(() => {
  if (!currentVariant.value?.addons) return [];
  return showAllAddons.value
    ? currentVariant.value.addons
    : currentVariant.value.addons.slice(0, SHOW_LIMIT);
});
const visibleExcludes = computed(() => {
  if (!currentVariant.value?.excludes) return [];
  return showAllExcludes.value
    ? currentVariant.value.excludes
    : currentVariant.value.excludes.slice(0, SHOW_LIMIT);
});

const addonsSum = computed<number>(() =>
  selectedAddons.value.reduce((sum, addonId) => {
    const addon = (currentVariant.value as any).addons?.find((a: any) => a.id === addonId);
    return sum + (Number(addon?.price) ?? 0);
  }, 0),
);

const totalPrice = computed<number>(
  () => Number(currentVariant.value.price) + Number(addonsSum.value),
);

const branchId = computed(() =>
  institutionStore.selectedBranch?.id ? String(institutionStore.selectedBranch.id) : null,
);

const getIds = (arr: any[]) =>
  (arr || [])
    .map((a) => (typeof a === 'object' && a !== null && 'id' in a ? String(a.id) : String(a)))
    .sort();

const productObj = computed(() => ({
  id: String(currentVariant.value.id),
  name: currentVariant.value.name,
  price: totalPrice.value,
  img: [
    ...((cartStore.selectedDish as any)?.type === 'group'
      ? (cartStore.selectedDish as any).img || []
      : (currentVariant.value as any).img || []),
  ],
}));

const addonsArr = computed(() => getIds(selectedAddons.value).map(String)) as any;
const excludesArr = computed(() => getIds(selectedExcludes.value).map(String)) as any;

// Реактивные переменные из useCartProduct, как в карточке корзины
consola.debug('[MoreAboutDish] product:', productObj);
consola.debug('[MoreAboutDish] branchId:', branchId);
consola.debug('[MoreAboutDish] addons:', addonsArr);
consola.debug('[MoreAboutDish] excludes:', excludesArr);
const {
  quantity,
  increment,
  decrement,
  removeFromCart,
  inCart,
  addToCart: addToCartBase,
} = useCartProduct({
  product: productObj, // <= важно .value!
  branchId,  // <= важно .value!
  addons: addonsArr,
  excludes: excludesArr,
});

// Состояние загрузки
const isAddingToCart = ref(false);

// ─── Аналитика воронки карточки ──────────────────────────────────────────────
// Та же воронка, что и в новой карточке: этот вариант открывается из новостного
// слайдера, и без него шаги промо-баннеров выпали бы из отчёта.
const { trackAdded, trackFailed } = useProductModalAnalytics({
  currentVariant: currentVariant as any,
  selectedAddons,
  selectedExcludes,
});

// Обернутая функция addToCart с отслеживанием загрузки
const addToCart = async (extraPayload = {}) => {
  isAddingToCart.value = true;
  try {
    await addToCartBase(extraPayload);
    trackAdded({ quantity: quantity.value || 1, total: totalPrice.value * (quantity.value || 1) });
  } catch (error) {
    consola.error('[MoreAboutDish] Error adding to cart:', error);
    trackFailed('unknown', error instanceof Error ? error.message : String(error));
  } finally {
    isAddingToCart.value = false;
  }
};

const handleQuantityChange = async (newQuantity: number) => {
  consola.debug('[MoreAboutDish] handleQuantityChange:', newQuantity);
  isAddingToCart.value = true;
  try {
    if (newQuantity > quantity.value) {
      await increment();
    } else if (newQuantity < quantity.value) {
      if (newQuantity <= 0) {
        await removeFromCart();
      } else {
        await decrement();
      }
    }
  } catch (error) {
    consola.error('[MoreAboutDish] Error changing quantity:', error);
  } finally {
    isAddingToCart.value = false;
  }
};

// Сброс при смене блюда или варианта
watch(
  () => cartStore.selectedDish,
  () => {
    consola.log('[MoreAboutDish] selectedDish changed, setting variant index to:', computedVariantIndex.value);
    // Устанавливаем индекс на основе preferredVariantId из store
    selectedVariantIndex.value = computedVariantIndex.value;
    
    selectedAddons.value = [];
    selectedExcludes.value = [];
    showAllAddons.value = false;
    showAllExcludes.value = false;
    showSettings.value = false;
    showProductInfo.value = false;
    
    // Сбрасываем флаги свайпа
    isDragging.value = false;
    isScrollingInner.value = false;
    dragOffset.value = 0;
  },
  { immediate: true },
);

// Отдельный watch для preferredVariantId (может измениться независимо от selectedDish)
watch(
  () => cartStore.preferredVariantId,
  (newPreferredId) => {
    consola.log('[MoreAboutDish] preferredVariantId changed to:', newPreferredId);
    consola.log('[MoreAboutDish] updating selectedVariantIndex to:', computedVariantIndex.value);
    selectedVariantIndex.value = computedVariantIndex.value;
  },
  { immediate: true },
);
watch(currentVariant, () => {
  selectedAddons.value = [];
  selectedExcludes.value = [];
  showAllAddons.value = false;
  showAllExcludes.value = false;
  showSettings.value = false;
  showProductInfo.value = false;
  
  // Сбрасываем флаги свайпа
  isDragging.value = false;
  isScrollingInner.value = false;
  dragOffset.value = 0;
  
  // Сбрасываем слайдер и останавливаем видео при смене варианта
  currentSlideIndex.value = 0;
  Object.values(videoRefs.value).forEach(video => {
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  });
  videoRefs.value = {};
  // Запускаем первое видео нового варианта
  nextTick(() => {
    playFirstVideo();
  });
});

onMounted(() => {
  emit('update:size', 'l');
  // Запускаем первое видео при загрузке модального окна
  nextTick(() => {
    playFirstVideo();
  });
  
  // Подписываемся на события swiper напрямую
  nextTick(() => {
    const swiperEl = swiperContainerRef.value;
    if (swiperEl) {
      consola.log('[MoreAboutDish] Setting up swiper event listeners');
      
      // Пробуем разные варианты названий событий
      swiperEl.addEventListener('slidechange', handleSlideChange);
      swiperEl.addEventListener('swiperslidechange', handleSlideChange);
      swiperEl.addEventListener('slideChange', handleSlideChange);
      
      consola.log('[MoreAboutDish] Swiper event listeners added');
    } else {
      consola.log('[MoreAboutDish] Swiper container not found');
    }
  });
});

onBeforeUnmount(() => {
  // Очищаем все слушатели событий
  const swiperEl = swiperContainerRef.value;
  if (swiperEl) {
    swiperEl.removeEventListener('slidechange', handleSlideChange);
    swiperEl.removeEventListener('swiperslidechange', handleSlideChange);
    swiperEl.removeEventListener('slideChange', handleSlideChange);
  }
  
  // Останавливаем все видео
  Object.values(videoRefs.value).forEach(video => {
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  });
});

const openBasket = () => {
  storeModal.close();
  cartStore.openBasket();
};

// Функции для обработки свайпа блока настроек
const handleTouchStart = (e: TouchEvent) => {
  touchStartY.value = e.touches[0].clientY;
  isDragging.value = true;
  
  // Проверяем, начинается ли касание внутри scrollInnerRef
  const scrollInner = scrollInnerRef.value;
  if (scrollInner) {
    const touchTarget = e.target as HTMLElement;
    const isInsideScrollInner = scrollInner.contains(touchTarget);
    
    // Если касание началось внутри scrollInner, устанавливаем флаг
    // Это означает, что текущий свайп предназначен для внутреннего скрола
    if (isInsideScrollInner) {
      isScrollingInner.value = true;
    }
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return;
  
  // Если текущий свайп предназначен для внутреннего скрола, блокируем движение родителя
  if (isScrollingInner.value) {
    return;
  }
  
  // Если флаг не установлен, значит касание началось вне scrollInner
  // Разрешаем движение родительского блока
  touchEndY.value = e.touches[0].clientY;
  const diff = touchEndY.value - touchStartY.value;
  
  // Разрешаем драг только вниз
  if (diff > 0) {
    dragOffset.value = diff;
    // Опционально: можно добавить визуальный эффект движения
    if (settingsBlockRef.value) {
      settingsBlockRef.value.style.transform = `translateY(${Math.min(diff, 300)}px)`;
      settingsBlockRef.value.style.transition = 'none';
    }
  }
};

const handleTouchEnd = () => {
  if (!isDragging.value) return;
  
  const swipeDistance = dragOffset.value;
  
  // Если свайп больше 100px, закрываем блок
  if (swipeDistance > 100) {
    closeSettings();
  } else if (settingsBlockRef.value) {
    // Возвращаем блок на место
    settingsBlockRef.value.style.transform = 'translateY(0)';
    settingsBlockRef.value.style.transition = 'transform 0.3s ease';
  }
  
  // Сбрасываем все флаги и переменные
  isDragging.value = false;
  isScrollingInner.value = false; // Важно! Сбрасываем флаг при отпускании пальца
  dragOffset.value = 0;
  touchStartY.value = 0;
  touchEndY.value = 0;
};

const closeSettings = () => {
  // Добавляем класс для анимации закрытия
  if (settingsBlockRef.value) {
    settingsBlockRef.value.style.transform = 'translateY(100%)';
    settingsBlockRef.value.style.transition = 'transform 0.3s ease-in-out';
  }
  
  // Ждем завершения анимации перед скрытием
  setTimeout(() => {
    showSettings.value = false;
    if (settingsBlockRef.value) {
      settingsBlockRef.value.style.transform = '';
      settingsBlockRef.value.style.transition = '';
    }
    // Сбрасываем все флаги
    isDragging.value = false;
    isScrollingInner.value = false;
    dragOffset.value = 0;
    touchStartY.value = 0;
    touchEndY.value = 0;
  }, 300);
};

// Функции для управления показом информации о продукте
const openProductInfo = () => {
  showProductInfo.value = true;
};

const closeProductInfo = () => {
  showProductInfo.value = false;
};

// Определяем тип медиа по расширению URL
const isVideo = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
};

// Получаем массив всех медиа (изображения и видео) из текущего варианта
const mediaItems = computed(() => {
  return currentVariant.value?.img || [];
});

// Обработчик смены слайда
const handleSlideChange = async (event: any) => {
  const swiper = event?.detail?.[0];
  if (!swiper) return;
  
  const newIndex = swiper.activeIndex;
  const oldIndex = currentSlideIndex.value;
  
  consola.log('[MoreAboutDish] Slide changed from', oldIndex, 'to', newIndex);
  
  currentSlideIndex.value = newIndex;
  
  // Останавливаем предыдущее видео
  const oldVideo = videoRefs.value[oldIndex];
  if (oldVideo) {
    consola.log('[MoreAboutDish] Stopping old video at index:', oldIndex);
    oldVideo.pause();
    oldVideo.currentTime = 0;
  }
  
  // Запускаем новое видео, если это видео
  await nextTick();
  const newVideo = videoRefs.value[newIndex];
  const newMediaItem = mediaItems.value[newIndex];
  
  if (newVideo && newMediaItem && isVideo(newMediaItem.url)) {
    consola.log('[MoreAboutDish] Found video at new index:', newIndex, 'readyState:', newVideo.readyState);
    
    const tryPlay = async (attempts = 3) => {
      for (let i = 0; i < attempts; i++) {
        try {
          // Сбрасываем время на начало
          newVideo.currentTime = 0;
          // Пытаемся запустить видео
          const playPromise = newVideo.play();
          if (playPromise !== undefined) {
            await playPromise;
            consola.log('[MoreAboutDish] Video started playing at index:', newIndex);
            return true;
          }
        } catch (error) {
          consola.error(`[MoreAboutDish] Error playing video (attempt ${i + 1}):`, error);
          if (i < attempts - 1) {
            // Ждем немного перед следующей попыткой
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      return false;
    };
    
    await tryPlay();
  } else {
    consola.log('[MoreAboutDish] No video at new index:', newIndex);
  }
};

// Автоматическое воспроизведение видео при загрузке первого слайда
const playFirstVideo = async () => {
  await nextTick();
  const firstVideo = videoRefs.value[0];
  const firstMediaItem = mediaItems.value[0];
  
  consola.log('[MoreAboutDish] playFirstVideo called, video exists:', !!firstVideo, 'is video:', firstMediaItem ? isVideo(firstMediaItem.url) : false);
  
  if (firstVideo && firstMediaItem && isVideo(firstMediaItem.url)) {
    try {
      consola.log('[MoreAboutDish] Attempting to play first video, readyState:', firstVideo.readyState);
      const playPromise = firstVideo.play();
      if (playPromise !== undefined) {
        await playPromise;
        consola.log('[MoreAboutDish] First video started playing');
      }
    } catch (error) {
      consola.error('[MoreAboutDish] Error playing first video:', error);
    }
  }
};

// Установка ref для видео элементов
const setVideoRef = (el: any, index: number) => {
  if (el) {
    consola.log('[MoreAboutDish] setVideoRef called for index:', index);
    videoRefs.value[index] = el;
    // Если это первое видео и оно только что загрузилось, запускаем его
    if (index === 0 && isVideo(mediaItems.value[0]?.url)) {
      consola.log('[MoreAboutDish] First video ref set, scheduling playFirstVideo');
      nextTick(() => {
        playFirstVideo();
      });
    }
  }
};

// Обработчик загрузки метаданных видео
const handleVideoLoadedMetadata = async (event: Event, index: number) => {
  const video = event.target as HTMLVideoElement;
  consola.log('[MoreAboutDish] Video loaded metadata, index:', index, 'currentSlide:', currentSlideIndex.value);
  
  // Если это видео на текущем слайде, запускаем его
  if (index === currentSlideIndex.value) {
    try {
      consola.log('[MoreAboutDish] Playing video on current slide:', index);
      video.currentTime = 0;
      await video.play();
      consola.log('[MoreAboutDish] Video playing successfully on slide:', index);
    } catch (error) {
      consola.error('[MoreAboutDish] Error playing video on loadedmetadata:', error);
    }
  } else {
    consola.log('[MoreAboutDish] Video loaded but not on current slide, skipping autoplay');
  }
};

// Дополнительный сброс слайдера при смене блюда (на всякий случай)
watch(
  () => cartStore.selectedDish,
  () => {
    currentSlideIndex.value = 0;
    // Очищаем refs видео
    Object.values(videoRefs.value).forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    videoRefs.value = {};
  },
);

// Методы для переключения добавок и исключений в мобильной версии
const toggleAddon = (addonId: number) => {
  const index = selectedAddons.value.indexOf(addonId);
  if (index > -1) {
    selectedAddons.value.splice(index, 1);
  } else {
    selectedAddons.value.push(addonId);
  }
};

const toggleExclude = (excludeId: number) => {
  const index = selectedExcludes.value.indexOf(excludeId);
  if (index > -1) {
    selectedExcludes.value.splice(index, 1);
  } else {
    selectedExcludes.value.push(excludeId);
  }
};
</script>

<template>
  <div class="moreAboutDish">
    <div class="moreAboutDish__grid">
      <!-- Медиа слайдер (изображения и видео) -->
      <div class="moreAboutDish__images-container">
        <ClientOnly>
          <!-- Одно изображение - без слайдера -->
          <div v-if="mediaItems.length === 1" class="moreAboutDish__img">
            <video
              v-if="isVideo(mediaItems[0].url)"
              :ref="(el) => setVideoRef(el, 0)"
              :src="mediaItems[0].url"
              playsinline
              muted
              loop
              autoplay
              preload="auto"
              class="moreAboutDish__media"
              :alt="currentVariant.name"
            />
            <img
              v-else
              :src="mediaItems[0].url"
              loading="lazy"
              class="moreAboutDish__media"
              :alt="currentVariant.name"
            />
          </div>
          
          <!-- Несколько изображений - со слайдером -->
          <div v-else-if="mediaItems.length > 1" class="moreAboutDish__slider">
            <swiper-container
              ref="swiperContainerRef"
              :space-between="10"
              :slides-per-view="1"
              :loop="false"
              :pagination="{ clickable: true }"
              class="moreAboutDish__swiper"
              @swiperslidechange="handleSlideChange"
            >
              <swiper-slide
                v-for="(media, index) in mediaItems"
                :key="media.key || index"
                class="moreAboutDish__slide"
              >
                <div class="moreAboutDish__img">
                  <!-- Видео -->
                  <video
                    v-if="isVideo(media.url)"
                    :ref="(el) => setVideoRef(el, index)"
                    :src="media.url"
                    playsinline
                    muted
                    loop
                    preload="auto"
                    class="moreAboutDish__media"
                    :alt="currentVariant.name"
                    @loadedmetadata="handleVideoLoadedMetadata($event, index)"
                  />
                  <!-- Изображение -->
                  <img
                    v-else
                    :src="media.url"
                    loading="lazy"
                    class="moreAboutDish__media"
                    :alt="currentVariant.name"
                  />
                </div>
              </swiper-slide>
            </swiper-container>
          </div>
          
          <!-- Плейсхолдер если нет медиа -->
          <div v-else class="moreAboutDish__img">
            <img :src="placeholderImg" loading="lazy" class="moreAboutDish__media" :alt="currentVariant.name" />
          </div>
        </ClientOnly>
      </div>

      <div class="moreAboutDish__content moreAboutDish-content">

        <!-- Title & Description -->
        <div class="moreAboutDish-content__title">
          {{ currentVariant.name }}
        </div>
        <div class="flex gap-3 justify-between">
          <ProductBadge :tag="currentVariant?.tag ?? currentVariant?.teg" size="md" class-name="mt-3"/>
          <!-- Кнопка "Состав" для мобильных -->
          <div
            v-if="currentVariant && (currentVariant.kbju?.proteins || currentVariant.description || currentVariant.weight)"
            class="moreAboutDish-content__composition-button"
          >
            <button
              class="flex items-center justify-center gap-2  text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              @click="openProductInfo"
            >
              <span class="text-base font-medium">Состав</span>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
        <div
          v-if="currentVariant.stop_list"
          class="text-red-500 text-base font-semibold my-2"
        >
          Товар временно недоступен для заказа
        </div>
        <div class="moreAboutDish-content__description moreAboutDish-content__description--desktop">
          {{ currentVariant.description }}
        </div>
        <div class="moreAboutDish-content__info moreAboutDish-content__info--desktop">
          <span v-if="currentVariant.amount">{{ currentVariant.amount }} шт.</span>
          <span v-if="currentVariant.volume">{{ currentVariant.volume }} мл.</span>
          <span v-if="currentVariant.weight">{{ currentVariant.weight }} г.</span>
        </div>
        <!-- Варианты (группа) -->
        <div v-if="(cartStore.selectedDish as any)?.type === 'group'" class="mt-4">
          <div v-if="(cartStore.selectedDish as any).is_radio_button" class="flex flex-col gap-4 my-4">
            <div
              v-for="(variant, idx) in (cartStore.selectedDish as any).variants"
              :key="variant.id"
              class="flex items-center gap-4"
            >
              <input
                :id="'radio-variant-' + variant.id"
                v-model="selectedVariantIndex"
                type="radio"
                :value="idx"
                class="form-radio h-5 w-5 accent-gray-800"
              />
              <label
                :for="'radio-variant-' + variant.id"
                class="cursor-pointer text-base w-full py-2"
              >
                {{ productVariantLabel(variant) }}
              </label>
            </div>
          </div>
          <UiKitTabs
            v-else
            v-model="selectedVariantIndex"
            :items="variantTabs"
            variant="solid"
            color="default"
            size="lg"
            full-width
            :panel="false"
            class="w-full"
          />
        </div>
        
        <!-- Оверлей для затемнения фона -->
        <div 
          v-if="showSettings"
          class="moreAboutDish-content__overlay"
          @click="closeSettings"
        ></div>
        
        <!-- Блок настроек: скрыт на мобильных, пока не нажата кнопка "Настроить" -->
        <div 
          v-if="showSettings"
          ref="settingsBlockRef"
          class="moreAboutDish-content__scroll moreAboutDish-content__scroll--mobile"
          @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
        >
          <!-- Заголовок с индикатором для свайпа и кнопкой закрытия -->
          <div 
            class="flex flex-col mb-4 pb-4 border-b border-gray-300 dark:border-gray-700"
          >
            <!-- Индикатор свайпа (полоска сверху) -->
            <div class="flex justify-center mb-3">
              <div class="w-12 h-1 bg-gray-400 dark:bg-gray-600 rounded-full"></div>
            </div>
            
            <!-- Заголовок и кнопка закрытия -->
            <div class="flex justify-between items-center">
              <div class="text-2xl font-semibold text-white">{{ currentVariant.name }}</div>
            </div>
          </div>
          
          <!-- Контент с прокруткой -->
          <div ref="scrollInnerRef" class="moreAboutDish-content__scroll-inner">
          <!-- Addons -->
          <div v-if="currentVariant.addons && currentVariant.addons.length" class="moreAboutDish-content-addons">
            <div class="text-md font-semibold my-2 text-white">Добавить по вкусу</div>
            
            <!-- Desktop: список -->
            <ul class="hidden md:grid grid-cols-1 gap-4">
              <li v-for="addon in visibleAddons" :key="addon.id" class="flex items-center gap-3 justify-between">
                <img
                  v-if="addon.img && addon.img.url"
                  :src="addon.img.url"
                  :alt="addon.name"
                  class="w-8 h-8 object-container rounded"
                />
                <div class="font-normal text-sm text-white">{{ addon.name }} (+{{ addon.price }} ₽)</div>
                <UiKitCheckbox v-model="selectedAddons" :value="addon.id" color="danger" size="lg" class="ml-auto" />
              </li>
            </ul>
            
            <!-- Mobile: карточки -->
            <div class="md:hidden grid grid-cols-4 gap-2">
              <div 
                v-for="addon in visibleAddons" 
                :key="addon.id"
                class="relative rounded-lg p-2 cursor-pointer transition-all duration-200 grid"
                :class="selectedAddons.includes(addon.id) 
                  ? 'bg-white' 
                  : 'bg-gray-700/40'"
                @click="toggleAddon(addon.id)"
              >
                <!-- Изображение -->
                <div class="w-full h-12 mb-2 flex items-center justify-center">
                  <img
                    v-if="addon.img && addon.img.url"
                    :src="addon.img.url"
                    :alt="addon.name"
                    class="w-full h-auto object-content rounded"
                  />
                  <div v-else class="w-10 h-auto bg-gray-400 rounded flex items-center justify-center">
                    <span class="text-xs text-gray-600">?</span>
                  </div>
                </div>
                
                <!-- Название -->
                <div 
                  class="text-xs font-medium text-center leading-tight mb-1"
                  :class="selectedAddons.includes(addon.id) ? 'text-gray-800' : 'text-white'"
                >
                  {{ addon.name }}
                </div>
                
                <!-- Цена -->
                <div 
                  class="text-xs text-center mt-auto"
                  :class="selectedAddons.includes(addon.id) ? 'text-gray-600' : 'text-gray-300'"
                >
                  +{{ addon.price }} ₽
                </div>
                
                <!-- Индикатор выбора -->
                <div 
                  v-if="selectedAddons.includes(addon.id)"
                  class="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center"
                >
                  <svg class="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
            </div>
            <UButton
              v-if="currentVariant.addons.length > SHOW_LIMIT && !showAllAddons"
              variant="ghost"
              color="white"
              block
              size="sm"
              icon="heroicons:chevron-down-20-solid"
              trailing
              style="background: transparent"
              class="mt-2 w-full text-white"
              @click="showAllAddons = true"
            >
              Показать ещё
            </UButton>
          </div>

          <!-- Excludes -->
          <div v-if="currentVariant.excludes && currentVariant.excludes.length" class="moreAboutDish-content-addons">
            <div class="text-md font-semibold my-2 text-white">Убрать ингредиенты</div>
            
            <!-- Desktop: список -->
            <ul class="hidden md:grid grid-cols-1 gap-4">
              <li v-for="exclude in visibleExcludes" :key="exclude.id" class="flex items-center gap-3 justify-between">
                <img
                  v-if="exclude.img && exclude.img.url"
                  :src="exclude.img.url"
                  :alt="exclude.name"
                  class="w-8 h-8 object-container rounded"
                />
                <div class="font-normal text-sm text-white">
                  {{ exclude.name }}
                </div>
                <UiKitCheckbox v-model="selectedExcludes" :value="exclude.id" color="danger" size="lg" class="ml-auto" />
              </li>
            </ul>
            
            <!-- Mobile: теги с зачеркиванием -->
            <div class="md:hidden flex flex-wrap gap-2">
              <div 
                v-for="exclude in visibleExcludes" 
                :key="exclude.id"
                class="relative px-3 py-2 rounded-full cursor-pointer transition-all duration-200"
                :class="selectedExcludes.includes(exclude.id) 
                  ? 'bg-blue-600' 
                  : 'bg-blue-800/20'"
                @click="toggleExclude(exclude.id)"
              >
                <!-- Название с зачеркиванием для выбранных -->
                <div 
                  class="text-sm font-medium text-white text-center whitespace-nowrap"
                  :class="selectedExcludes.includes(exclude.id) ? 'line-through' : ''"
                >
                  {{ exclude.name }}
                </div>
                
                <!-- Индикатор выбора (X иконка) -->
                <div 
                  v-if="selectedExcludes.includes(exclude.id)"
                  class="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center"
                >
                  <svg class="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
            </div>
            <UButton
              v-if="currentVariant.excludes.length > SHOW_LIMIT && !showAllExcludes"
              variant="ghost"
              color="white"
              size="sm"
              block
              icon="heroicons:chevron-down-20-solid"
              trailing
              style="background: transparent"
              class="mt-2 w-full text-white"
              @click="showAllExcludes = true"
            >
              Показать ещё
            </UButton>
          </div>

          </div>
        </div>

        <!-- Блок настроек для десктопа (всегда виден) -->
        <div class="moreAboutDish-content__scroll moreAboutDish-content__scroll--desktop ">
          <!-- Addons -->
          <div v-if="currentVariant.addons && currentVariant.addons.length" class="moreAboutDish-content-addons">
            <div class="text-md font-semibold my-2">Дополнения</div>
            <ul class="grid grid-cols-1 gap-4">
              <li v-for="addon in visibleAddons" :key="addon.id" class="flex items-center gap-3 justify-between">
                <img
                  v-if="addon.img && addon.img.url"
                  :src="addon.img.url"
                  :alt="addon.name"
                  class="w-8 h-8 object-container rounded"
                />
                <div class="font-normal text-sm">{{ addon.name }} (+{{ addon.price }} ₽)</div>
                <UiKitCheckbox v-model="selectedAddons" :value="addon.id" color="danger" size="lg" class="ml-auto" />
              </li>
            </ul>
            <UButton
              v-if="currentVariant.addons.length > SHOW_LIMIT && !showAllAddons"
              variant="ghost"
              color="white"
              size="sm"
              icon="heroicons:chevron-down-20-solid"
              trailing

              class="mt-2 w-full hover:!bg-transparent"
              @click="showAllAddons = true"
            >
              Показать ещё
            </UButton>
          </div>

          <!-- Excludes -->
          <div v-if="currentVariant.excludes && currentVariant.excludes.length" class="moreAboutDish-content-addons ">
            <div class="text-md font-semibold my-2">Исключить ингредиенты</div>
            <ul class="grid grid-cols-1 gap-4">
              <li v-for="exclude in visibleExcludes" :key="exclude.id" class="flex items-center gap-3 justify-between">
                <img
                  v-if="exclude.img && exclude.img.url"
                  :src="exclude.img.url"
                  :alt="exclude.name"
                  class="w-8 h-8 object-container rounded"
                />
                <div class="font-normal text-sm">
                  {{ exclude.name }}
                </div>
                <UiKitCheckbox v-model="selectedExcludes" :value="exclude.id" color="danger" size="lg" class="ml-auto" />
              </li>
            </ul>
            <UButton
              v-if="currentVariant.excludes.length > SHOW_LIMIT && !showAllExcludes"
              variant="ghost"
              color="white"
              size="sm"
              icon="heroicons:chevron-down-20-solid"
              trailing

              class="mt-2 w-full hover:!bg-transparent"
              @click="showAllExcludes = true"
            >
              Показать ещё
            </UButton>
          </div>


        </div>

        <!-- Блок КБЖУ - скрыт на мобильных по умолчанию -->
        <div v-if="currentVariant.kbju?.proteins" class="moreAboutDish-content__kbju moreAboutDish-content__kbju--desktop">
          <div v-if="currentVariant.kbju?.proteins" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.proteins }}
            <span>Белки</span>
          </div>
          <div v-if="currentVariant.kbju?.fats" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.fats }}
            <span>Жиры</span>
          </div>
          <div v-if="currentVariant.kbju?.carbohydrates" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.carbohydrates }}
            <span>Углеводы</span>
          </div>
          <div v-if="currentVariant.kbju?.calories" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.calories }}
            <span>Калории</span>
          </div>
        </div>


        <!-- Кнопка "Настроить" для мобильных (показывается если есть дополнения или исключения) -->
        <div 
          v-if="!showSettings && ((currentVariant.addons && currentVariant.addons.length) || (currentVariant.excludes && currentVariant.excludes.length))"
          class="moreAboutDish-content__settings-button"
        >
          <UiButtonMain
            icon="i-lucide-pen-line"
            color="orange"
            size="md"
            block
            class="w-full shake-animation"
            label="Изменить состав блюда"
            @click="showSettings = true"
          />
        </div>

        <!-- Итоговая цена и действия -->
        <div class="moreAboutDish-content__bottom">
          <div class="w-full">
            <UiBaseCounter
              v-if="inCart && !isAddingToCart"
              :count-v="quantity"
              :price="totalPrice"
              :min-v="0"
              :max-v="99"
              class="w-full"
              size="md"
              @count="handleQuantityChange"
            />
            <UiButtonMain
              v-else
              color="red"
              size="xl"
              block
              class="w-full"
              :label="`${totalPrice}₽`"
              :loading="isAddingToCart"
              :disabled="currentVariant.stop_list || isAddingToCart"
              @click.stop="addToCart"
            />
          </div>
          <UiButtonMain
            icon="heroicons:shopping-cart-solid"
            color="grey"
            class="banner__button-request bg-gray-500 md:hidden"
            size="xl"
            label=""
            :trailing="true"
            @click.stop="openBasket"
          />
        </div>
      </div>
    </div>

    <!-- Модальное окно с информацией о составе и аллергенах -->
    <div 
      v-if="showProductInfo"
      class="moreAboutDish-content__product-info-modal"
      @click="closeProductInfo"
    >
      <div 
        class="moreAboutDish-content__product-info-content"
        @click.stop
      >
        <!-- Заголовок с кнопкой закрытия -->
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">О продукте</h3>
          <button
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            @click="closeProductInfo"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Описание -->
        <div v-if="currentVariant.description" class="mb-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ currentVariant.description }}
          </p>
        </div>

        <!-- Информация о весе/объеме/количестве -->
        <div v-if="currentVariant.weight || currentVariant.volume || currentVariant.amount" class="flex gap-3 mb-4 text-sm text-gray-600 dark:text-gray-400">
          <span v-if="currentVariant.amount">{{ currentVariant.amount }} шт.</span>
          <span v-if="currentVariant.volume">{{ currentVariant.volume }} мл.</span>
          <span v-if="currentVariant.weight">{{ currentVariant.weight }} г.</span>
        </div>

        <!-- КБЖУ -->
        <div v-if="currentVariant.kbju && (currentVariant.kbju.proteins || currentVariant.kbju.fats || currentVariant.kbju.carbohydrates || currentVariant.kbju.calories)" class="moreAboutDish-content__kbju mb-6">
          <div v-if="currentVariant.kbju?.proteins" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.proteins }}
            <span>Белки</span>
          </div>
          <div v-if="currentVariant.kbju?.fats" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.fats }}
            <span>Жиры</span>
          </div>
          <div v-if="currentVariant.kbju?.carbohydrates" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.carbohydrates }}
            <span>Углеводы</span>
          </div>
          <div v-if="currentVariant.kbju?.calories" class="moreAboutDish-content__kbju-text">
            {{ currentVariant.kbju.calories }}
            <span>Калории</span>
          </div>
        </div>

        <!-- Кнопка "Понятно" -->
        <button
          class="w-full py-3 px-4 bg-gray-800 dark:bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          @click="closeProductInfo"
        >
          Понятно
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.moreAboutDish {
  @apply p-2.5 bg-white dark:bg-gray-900 ;

  &__grid {
    @apply bg-sky-300 dark:bg-zinc-800 rounded-2xl pt-5 pb-4 px-5 md:py-8 md:px-8
    grid md:grid-cols-2 gap-5 max-md:h-[calc(100dvh-20px)]
    max-md:grid-rows-[auto_1fr] md:grid-rows-[auto_1fr] ;
  }

  &__images-container {
    @apply relative min-w-[0] h-fit;
    
    @media (max-width: 768px) {
      max-height: 40vh;
    }
  }

  &__slider {
    @apply w-full h-full;
  }

  &__swiper {
    @apply w-full h-full rounded-xl overflow-hidden;
    
    // CSS-переменные для стилизации пагинации Swiper Web Component
    --swiper-pagination-color: #ef4444; // red-500 для активной точки
    --swiper-pagination-bullet-inactive-color: #9ca3af; // gray-400 для неактивных
    --swiper-pagination-bullet-inactive-opacity: 1;
    --swiper-pagination-bullet-size: 10px;
    --swiper-pagination-bullet-horizontal-gap: 4px;
    --swiper-pagination-bottom: 8px;
  }

  &__slide {
    @apply w-full h-full;
  }

  &__img {
    @apply overflow-hidden rounded-xl flex justify-center items-center;
    width: 100%;
    aspect-ratio: 1 / 1;
    
    @media (max-width: 768px) {
      max-height: 40vh;
    }
  }

  &__media {
    @apply w-full h-full object-cover;
  }

  &-content {
    @apply text-start flex flex-col;
    overflow-x: hidden;

    &__scroll {
      @apply overflow-y-auto pl-2 pr-4 grid gap-4;
      
      // Десктопная версия - всегда видна
      &--desktop {
        @apply hidden md:grid md:max-h-[300px];
      }
      
      // Мобильная версия - показывается при нажатии на "Настроить"
      &--mobile {
        @apply hidden;
        
        @media (max-width: 680px) {
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 85px; // Оставляем место для кнопок внизу
          z-index: 52;
          padding: 20px;
          margin: 0;
          border-radius: 16px 16px 0 0;
          animation: slideUp 0.3s ease-out;
        }
      }
      
      // Контейнер для прокручиваемого контента внутри мобильного блока
      &-inner {
        // min-h-0 обязателен: flex-ребёнок с min-height:auto не сжимается ниже
        // своего контента → overflow-y-auto не срабатывает и карточки наезжают друг на друга
        @apply overflow-y-auto overflow-x-hidden flex-1 min-h-0 pr-2 grid gap-8;
      }
    }
    
    &__settings-button {
      @apply my-4 md:hidden;
      
      @media (min-width: 680px) {
        display: none;
      }
    }
    
    &__overlay {
      @apply hidden;
      
      @media (max-width: 680px) {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 51;
        backdrop-filter: blur(18px);
        -webkit-backdrop-filter: blur(18px);
        background-color: rgba(0, 0, 0, 0.3);
        animation: fadeIn 0.3s ease-out;
      }
    }

    &__title {
      @apply text-xl font-bold;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      width: 100%;
      height: 41px;
      min-height: fit-content;
    }

    &__description {
      @apply my-3 max-md:text-xs;
      
      &--desktop {
        @apply hidden md:block;
      }
    }

    &__info {
      @apply flex gap-3;
      
      &--desktop {
        @apply hidden md:flex;
      }
    }

    &__kbju {
      @apply flex justify-between gap-4 p-3 bg-neutral-800 text-white rounded-xl my-8;

      &-text {
        @apply flex flex-col text-center text-base;

        span {
          @apply text-xs;
        }
      }

      // Десктопная версия - всегда видна
      &--desktop {
        @apply hidden md:flex;
      }
    }

    &__composition-button {
      @apply my-4;
      display: block;
      
      @media (min-width: 768px) {
        display: none !important;
      }
      
      @media (max-width: 767px) {
        display: block !important;
      }
    }

    &__product-info-modal {
      @apply fixed inset-0  flex items-end justify-center p-4;
      background-color: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      z-index: 53;
      
      @media (min-width: 768px) {
        display: none;
      }
    }

    &__product-info-content {
      @apply w-full max-w-md bg-white dark:bg-gray-800 rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto;
      animation: slideUp 0.3s ease-out;
    }

    &__bottom {
      @apply flex justify-end items-center  gap-5 pt-5 mt-10 border-t
      border-b-blue-300 mt-auto;
      
      @media (max-width: 680px) {
        position: relative;
        z-index: 52;
        background: inherit;
      }
    }

    &__price {
      @apply text-2xl font-medium;
    }
  }
}

// Анимация появления блока настроек снизу вверх
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

// Анимация затухания оверлея
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Анимация покачивания для оранжевой кнопки с паузой 8 секунд
@keyframes shakeWithPause {
  // Качание занимает первые 0.5 секунды из 8 секунд цикла (0-6.25%)
  0% {
    transform: rotate(0deg);
  }
  1% {
    transform: rotate(-3deg);
  }
  2% {
    transform: rotate(3deg);
  }
  3% {
    transform: rotate(-3deg);
  }
  4% {
    transform: rotate(3deg);
  }
  5% {
    transform: rotate(-2deg);
  }
  6% {
    transform: rotate(0deg);
  }
  // Пауза до конца цикла (8 секунд)
  100% {
    transform: rotate(0deg);
  }
}

.shake-animation {
  animation: shakeWithPause 8s ease-in-out infinite;
}
</style>
